import React, { createContext, useContext, useState, useEffect } from 'react';
import { getLanguage, defaultLanguage, languages } from '../locales';
import { languagesAPI } from '../services/apiService';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState(defaultLanguage);
  const [languageData, setLanguageData] = useState(getLanguage(defaultLanguage));
  const [availableLanguages, setAvailableLanguages] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch active languages from backend
  const fetchActiveLanguages = async () => {
    try {
      const activeLanguages = await languagesAPI.getActive();
      const filteredLanguages = activeLanguages
        .filter(lang => languages[lang.code]) // Only include languages that exist in our locales
        .map(lang => ({
          code: lang.code,
          name: lang.name,
          dir: languages[lang.code].dir,
          content: languages[lang.code].content
        }));
      
      setAvailableLanguages(filteredLanguages);
      
      // If current language is not available, switch to first available language
      const isCurrentLanguageAvailable = filteredLanguages.some(lang => lang.code === currentLanguage);
      if (!isCurrentLanguageAvailable && filteredLanguages.length > 0) {
        const newLanguage = filteredLanguages[0];
        setCurrentLanguage(newLanguage.code);
        setLanguageData(newLanguage);
        localStorage.setItem('selectedLanguage', newLanguage.code);
        document.documentElement.dir = newLanguage.dir;
        document.documentElement.lang = newLanguage.code;
      }
    } catch (error) {
      console.warn('API not available, using fallback languages:', error.message);
      // Fallback to all languages if API fails
      const fallbackLanguages = Object.values(languages).map(lang => ({
        code: lang.code,
        name: lang.name,
        dir: lang.dir,
        content: lang.content
      }));
      setAvailableLanguages(fallbackLanguages);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActiveLanguages();
  }, []);

  useEffect(() => {
    // Load language from localStorage if available
    const savedLanguage = localStorage.getItem('selectedLanguage');
    if (savedLanguage && getLanguage(savedLanguage)) {
      setCurrentLanguage(savedLanguage);
      setLanguageData(getLanguage(savedLanguage));
    }
  }, []);

  const changeLanguage = (languageCode) => {
    const newLanguage = getLanguage(languageCode);
    if (newLanguage) {
      setCurrentLanguage(languageCode);
      setLanguageData(newLanguage);
      localStorage.setItem('selectedLanguage', languageCode);
      
      // Update document direction for RTL languages
      document.documentElement.dir = newLanguage.dir;
      document.documentElement.lang = languageCode;
    }
  };

  const t = (key) => {
    const keys = key.split('.');
    let value = languageData.content;
    
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        console.warn(`Translation key not found: ${key} in language ${currentLanguage}`);
        return key;
      }
    }
    
    return value || key;
  };

  const value = {
    currentLanguage,
    languageData,
    availableLanguages,
    loading,
    changeLanguage,
    t,
    refreshLanguages: fetchActiveLanguages
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}; 